import React, { useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';

import Spinner from '~/components/Common/Spinner';

import { useRenderFacebookContentImage } from '~/hooks/apis/public-offer-feed/useFacebookContentImages';
import { useGetFacebookContentTemplates } from '~/hooks/apis/public-offer-feed/useFacebookContentTemplates';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function RenderImageModal({ open, onClose }: Props) {
  const [templateId, setTemplateId] = useState('');
  const [offerId, setOfferId] = useState('');
  const [region, setRegion] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const renderMutation = useRenderFacebookContentImage();
  const { data: templatesData, isLoading: isLoadingTemplates } = useGetFacebookContentTemplates();

  // Handle different possible response structures
  const templates = useMemo(() => {
    if (!templatesData) return [];

    // result is directly an array according to the contract
    if (Array.isArray(templatesData.result)) {
      return templatesData.result;
    }

    // Fallback: if data itself is an array
    if (Array.isArray(templatesData)) {
      return templatesData;
    }

    return [];
  }, [templatesData]);

  const handleSubmit = async () => {
    if (!templateId || !offerId || !region) {
      enqueueSnackbar('Please fill in all required fields', { variant: 'error' });
      return;
    }

    try {
      await renderMutation.mutateAsync({
        template_id: templateId,
        offer_id: offerId,
        region,
      });
      enqueueSnackbar('Image render triggered successfully', { variant: 'success' });
      handleClose();
    } catch (error) {
      enqueueSnackbar(`Failed to trigger render: ${(error as Error).message}`, { variant: 'error' });
    }
  };

  const handleClose = () => {
    setTemplateId('');
    setOfferId('');
    setRegion('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Trigger Facebook Content Image Render</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <FormControl fullWidth required>
            <InputLabel>Template</InputLabel>
            <Select
              value={templateId}
              label="Template"
              onChange={(e) => setTemplateId(e.target.value)}
              disabled={isLoadingTemplates}
            >
              {isLoadingTemplates ? (
                <MenuItem disabled>Loading templates...</MenuItem>
              ) : templates.length === 0 ? (
                <MenuItem disabled>No templates available</MenuItem>
              ) : (
                templates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name || `Template ${template.id}`}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Offer ID"
            required
            value={offerId}
            onChange={(e) => setOfferId(e.target.value)}
            placeholder="Enter offer ID"
          />

          <TextField
            fullWidth
            label="Region"
            required
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            placeholder="Enter region code (e.g., AU, US, UK)"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={handleClose} disabled={renderMutation.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={renderMutation.isPending}>
          {renderMutation.isPending ? <Spinner size={15} inline /> : 'Trigger Render'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
